(function () {
  'use strict';

  if (window.LSCartState) return;

  const normalizers = new Map();
  let cachedCart = null;
  let cartRequest = null;
  let syncQueue = Promise.resolve();
  let pubSubBound = false;

  function getRootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  function isFullCart(cart) {
    return Boolean(
      cart &&
        Array.isArray(cart.items) &&
        typeof cart.item_count === 'number' &&
        typeof cart.total_price === 'number'
    );
  }

  async function parseResponse(response) {
    const text = await response.text();
    let data = {};

    try {
      data = text ? JSON.parse(text) : {};
    } catch (error) {
      data = {};
    }

    if (!response.ok || data.status) {
      const message =
        data.description ||
        data.message ||
        text ||
        window.cartStrings?.error ||
        'Unable to update the cart.';
      const requestError = new Error(message);
      requestError.data = data;
      throw requestError;
    }

    return data;
  }

  async function requestJSON(url, options = {}) {
    const requestOptions = {
      credentials: 'same-origin',
      ...options,
      headers: {
        Accept: 'application/json',
        ...(options.headers || {}),
      },
    };

    return parseResponse(await fetch(url, requestOptions));
  }

  function invalidate() {
    cachedCart = null;
  }

  async function getCart(options = {}) {
    const force = options.force === true;

    if (!force && isFullCart(cachedCart)) return cachedCart;

    if (cartRequest) {
      if (!force) return cartRequest;

      try {
        await cartRequest;
      } catch (error) {
        // A fresh request is started below after a failed or stale in-flight request.
      }
    }

    cartRequest = requestJSON(`${getRootUrl()}cart.js`)
      .then((cart) => {
        cachedCart = cart;
        return cart;
      })
      .finally(() => {
        cartRequest = null;
      });

    return cartRequest;
  }

  function registerNormalizer(name, normalizer) {
    if (!name || typeof normalizer !== 'function') return;
    normalizers.set(String(name), normalizer);
  }

  function unregisterNormalizer(name) {
    normalizers.delete(String(name));
  }

  async function runNormalizers(cart, context) {
    let currentCart = cart;
    let changed = false;

    for (const normalizer of normalizers.values()) {
      const result = await normalizer(currentCart, context || {});

      if (!result) continue;

      if (isFullCart(result)) {
        currentCart = result;
        continue;
      }

      if (isFullCart(result.cart)) currentCart = result.cart;
      if (result.changed === true) changed = true;
    }

    return { cart: currentCart, changed };
  }

  function dispatchCustomEvent(name, cart, source, extra = {}) {
    document.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          cartData: cart,
          source: source || 'ls-cart-state',
          ...extra,
        },
      })
    );
  }

  function notify(cart, source, options = {}) {
    if (!isFullCart(cart)) return;

    cachedCart = cart;
    const eventSource = source || 'ls-cart-state';

    dispatchCustomEvent('ls:cart-updated', cart, eventSource, {
      adjusted: options.adjusted === true,
    });

    if (
      options.publish === true &&
      typeof publish === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: eventSource,
        cartData: cart,
        variantId: options.variantId,
        skipSectionRefresh: options.skipSectionRefresh === true,
      });
    }
  }

  function emitAdjusted(cart, source) {
    dispatchCustomEvent('ls:cart-adjusted', cart, source || 'ls-cart-state-adjusted', {
      adjusted: true,
    });
  }

  function sync(options = {}) {
    const task = async () => {
      let cart = isFullCart(options.cartData) ? options.cartData : null;

      if (!cart) {
        if (options.invalidate === true) invalidate();
        cart = await getCart({ force: options.force === true });
      } else {
        cachedCart = cart;
      }

      const result = await runNormalizers(cart, {
        source: options.source || 'ls-cart-state',
      });

      cachedCart = result.cart;

      if (result.changed && options.emitAdjusted !== false) {
        emitAdjusted(result.cart, options.source || 'ls-cart-state-adjusted');
      }

      if (options.notify === true) {
        notify(result.cart, options.source, {
          publish: options.publish === true,
          skipSectionRefresh: options.skipSectionRefresh === true,
          adjusted: result.changed,
          variantId: options.variantId,
        });
      }

      return result;
    };

    syncQueue = syncQueue.then(task, task);
    return syncQueue;
  }

  function processExternalUpdate(event) {
    if (!event) return;

    const source = String(event.source || 'shopify-cart-update');
    if (source.indexOf('ls-cart-state') === 0) return;

    const suppliedCart = isFullCart(event.cartData) ? event.cartData : null;

    sync({
      cartData: suppliedCart,
      force: !suppliedCart,
      invalidate: !suppliedCart,
      source,
      notify: false,
      emitAdjusted: true,
    })
      .then((result) => {
        if (!result.changed) {
          notify(result.cart, source, { publish: false });
        }
      })
      .catch((error) => {
        console.error('[Love Smiths] Cart state synchronisation error:', error);
      });
  }

  function bindPubSub() {
    if (pubSubBound) return;
    if (
      typeof subscribe !== 'function' ||
      typeof PUB_SUB_EVENTS === 'undefined' ||
      !PUB_SUB_EVENTS.cartUpdate
    ) {
      return;
    }

    pubSubBound = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, processExternalUpdate);
  }

  window.LSCartState = {
    getCart,
    invalidate,
    isFullCart,
    notify,
    requestJSON,
    registerNormalizer,
    sync,
    unregisterNormalizer,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindPubSub, { once: true });
  } else {
    bindPubSub();
  }
})();
